import React, { useState } from "react";
import { useProfileParams } from "../../../../../../utils/common";
import { useGetBusinessPerformanceDetailsQuery } from "../../../../../../redux/services/businessPerformaceAPI";
import styles from "./ProductivityPerContributorTile.module.scss";
import KPITile from "../../../../../common/KPITile/KPITile";
import KPIDetailTile from "../../../../../common/KPIDetailTile/KPIDetailTile";
import Benchmark from "../../../../../common/Benchmark/Benchmark";
import Tooltip from "../../../../../common/Tooltip/Tooltip";
import { useTranslation } from "react-i18next";
import Icon from "../../../../../common/Icon/Icon";
import { useAppSelector } from "../../../../../../redux/hooks";
import GraphCard from "../../../../../common/GraphCard/GraphCard";
import InsightAI from "../../../../../common/InsightAI/InsightAI";
import Skeleton from "../../../../../common/Skeleton/Skeleton";
import isEmpty from "lodash/isEmpty";
import { useGetAGCAISummaryQuery } from "../../../../../../redux/services/agcAIAPI";
import { CurrencyFormatter } from "../../../../../../utils/currencyFormatter";
import { NumberFormatter } from "../../../../../../utils/NumberFormatter";

interface SummaryProp {
  summary?: any;
  loading?: boolean;
  affiliateCode?: string;
}

export const ProductivityPerContributorTile: React.FC<SummaryProp> = ({
  summary,
  loading = false,
  affiliateCode = "010",
}) => {
  const { t } = useTranslation();
  const benchmark = summary?.benchmark;
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);

  return (
    <KPITile
      title={t("kpiTitles.productivityPerContributor")}
      dataTestId="productivity-per-contributor-tile"
      loading={loading}
    >
      <div className={styles.tile_content}>
        <div className={styles.values_wrapper}>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>
              {t("kpiLabels.prodDemandSales")}
            </div>
            <div className={styles.value_content}>
              {summary?.metricSourceData?.[0]?.value !== null &&
              summary?.metricSourceData?.[0]?.value !== undefined
                ? String(
                    summary?.metricSourceData?.[0]?.dataType === "currency"
                      ? CurrencyFormatter(
                          summary?.metricSourceData?.[0]?.currency,
                          summary?.metricSourceData?.[0]?.value
                        )
                      : NumberFormatter(
                          userAffiliateCode,
                          summary?.metricSourceData?.[0]?.value
                        )
                  ).replace(/\.00$/, "")
                : "--"}
            </div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>/</div>
            <div className={styles.value_content}></div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>
              {t("kpiLabels.contributorCount")}
            </div>
            <div className={styles.value_content}>
              {summary?.metricSourceData?.[1]?.value
                ? NumberFormatter(
                    affiliateCode,
                    summary?.metricSourceData?.[1]?.value
                  )
                : "--"}
            </div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>=</div>
            <div className={styles.value_content}></div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>
              {t("kpiLabels.dollarContributor")}
            </div>
            <div
              className={`${styles.value_content} ${styles.value_highlight}`}
            >
              {summary?.metricSourceData?.[2]?.value !== null &&
              summary?.metricSourceData?.[2]?.value !== undefined
                ? String(
                    summary?.metricSourceData?.[2]?.dataType === "currency"
                      ? CurrencyFormatter(
                          summary?.metricSourceData?.[2]?.currency,
                          summary?.metricSourceData?.[2]?.value
                        )
                      : NumberFormatter(
                          userAffiliateCode,
                          summary?.metricSourceData?.[2]?.value
                        )
                  ).replace(/\.00$/, "")
                : "--"}
            </div>
          </div>
        </div>

        <div className={styles.bar_wrapper}>
          {
            <Benchmark
              minValue={benchmark?.min}
              maxValue={benchmark?.max}
              averageValue={benchmark?.marketAverage}
              medianValue={benchmark?.median}
              currentBenchmarkValue={summary?.kpiValue}
              quartileLine={[
                benchmark?.lowerQuartile ?? 0,
                benchmark?.upperQuartile ?? 0,
              ]}
              benchmarkDataType={summary?.benchmarkDataType}
              currency={summary?.kpiCurrency}
              isPercentValue={false}
            />
          }
          <div className={styles.bar_footer}>
            {t("market_average")}{" "}
            <span className={styles.bar_footer_value}>
              {String(
                CurrencyFormatter(
                  summary?.kpiCurrency,
                  benchmark?.marketAverage
                )
              ).replace(/\.00$/, "")}
            </span>
            <span>
              <Tooltip
                content={
                  <span>{t("kpiTooltips.productivityPerContributor")}</span>
                }
                width="250"
                height="auto"
                bottom="30px"
                left="685%"
              >
                <span>
                  <Icon
                    name="info_icon"
                    size={16}
                    color={currentTheme === "light" ? "#707070" : "#AEAEB2"}
                  />
                </span>
              </Tooltip>
            </span>
          </div>
        </div>
      </div>
    </KPITile>
  );
};

export const ProductivityPerContributorDetail: React.FC<{
  kpiCode?: string;
  onClose?: () => void;
}> = ({ kpiCode = "productivityPerContributor", onClose }) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();

  const { data: kpiDetailsResponse, isLoading } =
    useGetBusinessPerformanceDetailsQuery(
      {
        affiliateCode,
        aboNumber,
        payload: { kpis: [{ code: kpiCode }] } as any,
      },
      { skip: !onClose }
    );

  const { data: agcAISummary, isLoading: isLoadingAiSummary } =
    useGetAGCAISummaryQuery(
      {
        affiliateCode,
        aboNumber,
      },
      {
        skip: Number(affiliateCode) === 0 || aboNumber === 0,
        refetchOnMountOrArgChange: true,
      }
    );

  const detailData = kpiDetailsResponse?.body.kpis[0] || {};
  const monthlyViewCharts = detailData?.detail?.monthlyView?.chart || [];

  // Extract line chart data
  const lineChartData = monthlyViewCharts.find(
    (chart: any) => chart.chartType === "lineChart"
  );

  const lineChartDetailData = lineChartData?.chartDetail?.lineChartDetail;

  if (isLoading) return <Skeleton height="400px" />;

  const aiContent =
    agcAISummary?.body?.summary?.length > 300
      ? `${agcAISummary?.body?.summary?.slice(0, 300)}...`
      : agcAISummary?.body?.summary;
  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>
          {t("kpiTitles.productivityPerContributor")}
        </div>
        <div className={styles.desc}>
          {t("kpiDescription.productivityPerContributor")}
        </div>
      </div>
    </div>
  );

  const leftContent = (
    <div className={styles.left_content}>
      {isEmpty(lineChartDetailData) ? (
        <div className={styles.no_data}>{t("noDataAvailable")}</div>
      ) : (
        <GraphCard
          lineChartData={lineChartDetailData}
          currency={lineChartDetailData?.kpiCurrency || "USD"}
          chartSummaryData={lineChartData?.chartSummary}
        />
      )}
    </div>
  );

  const rightContent = isLoadingAiSummary ? (
    <Skeleton height="200px" />
  ) : (
    <InsightAI
      updateDate=""
      content={aiContent}
      title={t("ai_summary")}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={24}
      titleSize={14}
    />
  );

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="productivity-per-contributor-detail"
    />
  );
};

export default ProductivityPerContributorTile;
